import { NavLink, useNavigate } from 'react-router-dom'
import { Home, Search, Bell, Mail, Unlock, Bookmark, User, Settings, PlusCircle, Flame, TrendingUp, Video, LayoutDashboard, Star, ShieldAlert, Headset, Users, Crown, Shield, HelpCircle } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import { useNotificationStore } from '../../stores/notificationStore'
import { useCommandModal } from '../../stores/commandModalStore'
import Avatar from '../ui/Avatar'
import { cn } from '../../lib/utils'

const publicNavItems = [
  { to: '/', icon: Home, label: 'Home' },
  { to: '/explore', icon: TrendingUp, label: 'Explore' },
  { to: '/reels', icon: Video, label: 'Reels' },
  { to: '/livestreams', icon: Video, label: 'Livestreams' },
]

const authNavItems = [
  { to: '/notifications', icon: Bell, label: 'Notifications', countKey: 'notifications' },
  { to: '/messages', icon: Mail, label: 'Messages' },
  { to: '/unlocks', icon: Unlock, label: 'My Unlocks' },
  { to: '/bookmarks', icon: Bookmark, label: 'Bookmarks' },
]

function SidebarLink({ to, icon: Icon, label, count = 0, badge }) {
  const accessibleLabel = count > 0 ? `${label} (${count} new)` : label

  return (
    <NavLink
      to={to}
      aria-label={accessibleLabel}
      title={label}
      className={({ isActive }) =>
        cn(
          'group flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-150',
          isActive
            ? 'bg-white/[0.08] text-white font-semibold glow-sm'
            : 'text-zinc-400 hover:bg-white/[0.04] hover:text-zinc-200'
        )
      }
    >
      {({ isActive }) => (
        <>
          <div className="relative flex-shrink-0">
            <Icon size={18} strokeWidth={isActive ? 2.5 : 1.8} />
            {count > 0 && (
              <div className="absolute -top-1 -right-1.5 min-w-[16px] h-4 bg-red-500 rounded-full flex items-center justify-center glow-red-sm">
                <span className="text-[9px] font-bold text-white px-1">
                  {count > 99 ? '99+' : count}
                </span>
              </div>
            )}
          </div>
          <span className="hidden xl:block text-[13px] flex-1">{label}</span>
          {badge && (
            <span className="hidden xl:inline-flex text-[10px] px-1.5 py-0.5 rounded-md bg-emerald-500/15 text-emerald-400 font-semibold">
              {badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  )
}

function SidebarAction({ icon: Icon, label, badge, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="group flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-150 text-zinc-400 hover:bg-white/[0.04] hover:text-zinc-200 w-full text-left cursor-pointer"
    >
      <div className="relative flex-shrink-0">
        <Icon size={18} strokeWidth={1.8} />
      </div>
      <span className="hidden xl:block text-[13px] flex-1">{label}</span>
      {badge && (
        <span className="hidden xl:inline-flex text-[10px] px-1.5 py-0.5 rounded-md bg-emerald-500/15 text-emerald-400 font-semibold">
          {badge}
        </span>
      )}
    </button>
  )
}

export default function Sidebar() {
  const { profile, user } = useAuthStore()
  const { unreadCount } = useNotificationStore()
  const openModal = useCommandModal(s => s.open)
  const navigate = useNavigate()

  return (
    <div className="hidden md:flex md:flex-col w-16 xl:w-[220px] flex-shrink-0 h-screen">
      <nav className="flex flex-col py-4 px-2 xl:px-3 min-h-full">

        {/* Logo / Brand — like company header in Kangaroo */}
        <div className="px-3 mb-1">
          <NavLink to="/" aria-label="Heatly Home" className="flex items-center gap-2.5 py-2">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0 glow-md">
              <Flame className="text-red-600 fill-red-600" size={16} />
            </div>
            <div className="hidden xl:block min-w-0">
              <p className="text-sm font-black tracking-tight bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent leading-tight">
                HEATLY
              </p>
              {user && profile && (
                <p className="text-[10px] text-zinc-600 truncate leading-tight">
                  @{profile.username}
                </p>
              )}
            </div>
          </NavLink>
        </div>

        {/* Divider */}
        <div className="h-px bg-zinc-800/40 mx-3 mb-3" />

        {/* Main Navigation */}
        <div className="flex-1 space-y-0.5">
          {publicNavItems.map(item => (
            <SidebarLink
              key={item.to}
              to={item.to}
              icon={item.icon}
              label={item.label}
            />
          ))}

          {user && (
            <>
              <div className="h-px bg-zinc-800/40 my-2.5 mx-3" />
              {authNavItems.map(item => (
                <SidebarLink
                  key={item.to}
                  to={item.to}
                  icon={item.icon}
                  label={item.label}
                  count={item.countKey === 'notifications' ? unreadCount : 0}
                />
              ))}

              <div className="h-px bg-zinc-800/40 my-2.5 mx-3" />

              {/* Heatly+ */}
              <SidebarAction icon={Crown} label="Heatly+" badge="PRO" onClick={() => openModal('plus')} />

              {profile?.is_creator ? (
                <>
                  <SidebarAction icon={LayoutDashboard} label="Dashboard" onClick={() => openModal('dashboard')} />
                  <SidebarAction icon={Shield} label="Partner" badge="New" onClick={() => openModal('partner')} />
                </>
              ) : (
                <SidebarLink to="/become-creator" icon={Star} label="Become a Creator" />
              )}

              {/* Staff sections */}
              {profile?.system_role && (
                <>
                  <div className="h-px bg-zinc-800/40 my-2.5 mx-3" />
                  {['admin', 'support', 'support_lead'].includes(profile.system_role) && (
                    <SidebarAction icon={Headset} label="Support" onClick={() => openModal('support')} />
                  )}
                  {['admin', 'manager', 'management_lead'].includes(profile.system_role) && (
                    <SidebarAction icon={Users} label="Management" onClick={() => openModal('management')} />
                  )}
                  {profile.system_role === 'admin' && (
                    <SidebarAction icon={ShieldAlert} label="Admin" onClick={() => openModal('admin')} />
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Bottom section — Create + Settings + Profile */}
        <div className="mt-auto space-y-0.5">
          {user ? (
            <>
              {/* Create Post — creators only */}
              {profile?.is_creator && (
                <button
                  type="button"
                  aria-label="Create Post"
                  title="Create Post"
                  onClick={() => navigate('/')}
                  className="mb-3 w-full bg-red-600 hover:bg-red-500 text-white text-sm font-bold py-2.5 rounded-lg transition-all flex items-center justify-center gap-2 active:scale-[0.97] cursor-pointer glow-red"
                >
                  <PlusCircle size={16} />
                  <span className="hidden xl:inline">Create Post</span>
                </button>
              )}

              <div className="h-px bg-zinc-800/40 mx-3 mb-2" />

              {/* Settings */}
              <SidebarAction icon={Settings} label="Settings" onClick={() => openModal('settings')} />

              {/* Profile */}
              {profile && (
                <NavLink
                  to={`/@${profile.username}`}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-150',
                      isActive
                        ? 'bg-white/[0.08]'
                        : 'hover:bg-white/[0.04]'
                    )
                  }
                >
                  <Avatar src={profile.avatar_url} alt={profile.display_name} size="sm" />
                  <div className="hidden xl:block min-w-0">
                    <p className="text-xs font-semibold text-white truncate leading-tight">{profile.display_name}</p>
                    <p className="text-[10px] text-zinc-500 truncate leading-tight">@{profile.username}</p>
                  </div>
                </NavLink>
              )}
            </>
          ) : (
            <NavLink
              to="/auth"
              aria-label="Sign In"
              title="Sign In"
              className="w-full bg-red-600 hover:bg-red-500 text-white text-sm font-bold py-2.5 rounded-lg transition-all flex items-center justify-center gap-2 active:scale-[0.97] cursor-pointer glow-red"
            >
              <User size={16} />
              <span className="hidden xl:inline">Sign In</span>
            </NavLink>
          )}
        </div>
      </nav>
    </div>
  )
}
